import { useState, useEffect, useRef } from "react";
import { collection, onSnapshot } from "firebase/firestore";
import { db } from "../../util/firebase";
import { executeApi } from "../../util/apiHelper";



// Static order sub-routes to ignore when extracting orderId
const STATIC_ORDER_ROUTES = new Set([
  "",
  "list",
  "approved",
  "paid",
  "pending",
  "canceled",
  "mail",
  "offline",
  "line",
  "reserve",
  "cart",
  "create",
  "tax-request",
]);

/**
 * Extracts orderId from a route path (e.g. /orders/66d8f1e9... -> 66d8f1e9...)
 */
export const extractOrderIdFromPath = (path) => {
  if (!path || typeof path !== "string") return null;
  const clean = path.split("?")[0].trim();
  if (!clean.startsWith("/orders/")) return null;
  const sub = clean.replace("/orders/", "").split("/")[0].trim();
  if (!sub || STATIC_ORDER_ROUTES.has(sub.toLowerCase())) return null;
  return sub;
};

/**
 * Format relative time (e.g. 15 วิที่แล้ว, 1 นาทีที่แล้ว)
 */
export const formatRelativeTime = (lastActiveMs) => {
  if (!lastActiveMs) return "เมื่อสักครู่";
  const diffSec = Math.max(0, Math.floor((Date.now() - lastActiveMs) / 1000));
  if (diffSec < 10) return "เมื่อสักครู่";
  if (diffSec < 60) return `${diffSec} วิที่แล้ว`;
  const diffMin = Math.floor(diffSec / 60);
  return `${diffMin} นาทีที่แล้ว`;
};

/**
 * Hook to register order viewing directly into AdminPresence collection (Allowed in firestore.rules)
 */
export const useRegisterOrderViewing = (orderId, profileState) => {
  useEffect(() => {
    if (!db || !orderId) return;

    const adminId = profileState?.userId
      ? String(profileState.userId)
      : profileState?.username
      ? String(profileState.username)
      : profileState?._id
      ? String(profileState._id)
      : null;

    if (!adminId) return;

    const rawName =
      profileState?.displayName ||
      [profileState?.firstName, profileState?.lastName]
        .filter(Boolean)
        .join(" ")
        .trim() ||
      profileState?.name ||
      profileState?.username ||
      `แอดมิน (${adminId.slice(-4)})`;
    const firstName = profileState?.firstName || profileState?.displayName || rawName;
    const profileImage = profileState?.profileImage || "";

    // 1. Immediately register on mount
    executeApi("utils-updateAdminPresence", {
      adminId,
      presenceData: {
        userId: adminId,
        name: rawName,
        firstName,
        profileImage,
        currentPath: `/orders/${orderId}`,
        viewingOrderId: String(orderId),
        viewingOrderMs: Date.now(),
        lastActiveMs: Date.now(),
      },
    }).catch(() => {});

    // 2. Heartbeat every 2 minutes while looking at this order (reduced from 20s)
    const interval = setInterval(() => {
      executeApi("utils-updateAdminPresence", {
        adminId,
        presenceData: {
          viewingOrderId: String(orderId),
          viewingOrderMs: Date.now(),
          lastActiveMs: Date.now(),
        },
      }).catch(() => {});
    }, 2 * 60 * 1000);

    const handleBeforeUnload = () => {
      executeApi("utils-updateAdminPresence", {
        adminId,
        presenceData: { viewingOrderId: null },
      }).catch(() => {});
    };

    window.addEventListener("beforeunload", handleBeforeUnload);

    return () => {
      clearInterval(interval);
      window.removeEventListener("beforeunload", handleBeforeUnload);
      executeApi("utils-updateAdminPresence", {
        adminId,
        presenceData: { viewingOrderId: null },
      }).catch(() => {});
    };
  }, [orderId, profileState]);

};

/**
 * Custom Hook: Real-time map of active admins viewing specific orderIds
 * High-performance: Only re-renders when data actually changes
 */
export const useOrderAdminsPresence = (currentAdminId) => {
  const [presenceMap, setPresenceMap] = useState({});
  const prevMapJsonRef = useRef("");

  useEffect(() => {
    if (!db) return;

    const unsub = onSnapshot(
      collection(db, "AdminPresence"),
      (snapshot) => {
        const now = Date.now();
        const nextOrderMap = {};

        snapshot.forEach((docSnap) => {
          const data = docSnap.data();
          const adminId = String(data.userId || docSnap.id);
          const isSelf = currentAdminId && String(currentAdminId) === adminId;

          // Check if admin is active within last 2 minutes and not offline
          const lastActiveMs =
            data.lastActiveMs ||
            (data.lastActive?.toMillis ? data.lastActive.toMillis() : 0) ||
            now;
          const isFresh = now - lastActiveMs < 2 * 60 * 1000;
          const isOnline = data.status !== "offline";

          if (isFresh && isOnline) {
            // Check viewingOrderId first, then fallback to currentPath
            const orderId =
              data.viewingOrderId || extractOrderIdFromPath(data.currentPath || "");

            if (orderId) {
              const rawName =
                data.displayName ||
                data.name ||
                [data.firstName, data.lastName].filter(Boolean).join(" ").trim() ||
                `แอดมิน (${adminId.slice(-4)})`;
              const rawFirst = data.firstName || data.displayName || rawName;

              const adminInfo = {
                id: adminId,
                isSelf,
                name: isSelf ? `${rawName} (คุณ)` : rawName,
                firstName: isSelf ? `${rawFirst} (คุณ)` : rawFirst,
                profileImage: data.profileImage || "",
                lastActiveMs,
              };

              const lowerId = String(orderId).toLowerCase();
              if (!nextOrderMap[orderId]) {
                nextOrderMap[orderId] = [];
              }
              nextOrderMap[orderId].push(adminInfo);

              if (lowerId !== String(orderId)) {
                if (!nextOrderMap[lowerId]) {
                  nextOrderMap[lowerId] = [];
                }
                nextOrderMap[lowerId].push(adminInfo);
              }
            }
          }
        });

        // Only trigger state change if data actually changed
        const nextJson = JSON.stringify(nextOrderMap);
        if (nextJson !== prevMapJsonRef.current) {
          prevMapJsonRef.current = nextJson;
          setPresenceMap(nextOrderMap);
        }
      },
      (err) => console.warn("AdminPresence listener error:", err)
    );

    return () => unsub();
  }, [currentAdminId]);

  return { presenceMap };
};
